import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, useForm, useFormContext, useWatch} from 'react-hook-form'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {generateYearOptions, userQualificationToLabel} from '@/features/users/utils'
import {InputText} from '@/components/commons/input-text/InputText'
import {useRef} from 'react'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {UserEducationFormValidationSchema, UserQualificationFormValidationSchema} from '@/features/users/types'

export const UserQualificationFormModal = ({
    onClose,
    selectedQualification,
    title
}: {
    onClose: () => void
    selectedQualification: number | null
    title: string
}) => {
    const {t} = useTranslation()
    const modalBodyRef = useRef(null)

    // parent methods
    const parentForm = useFormContext<UserEducationFormValidationSchema>()

    // parent form observer
    const qualifications = useWatch({control: parentForm.control, name: 'qualifications'})

    // modal form methods
    const form = useForm<UserQualificationFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserQualificationFormValidationSchema),
        defaultValues: selectedQualification != null ? qualifications?.[selectedQualification] : {}
    })

    const handleSubmit = form.handleSubmit(formValues => {
        if (selectedQualification != null && qualifications) {
            const updatedQualifications = [...qualifications]
            updatedQualifications[selectedQualification] = formValues
            parentForm.setValue('qualifications', updatedQualifications, {shouldDirty: true})
        } else {
            parentForm.setValue('qualifications', qualifications ? qualifications.concat(formValues) : [formValues], {
                shouldDirty: true
            })
        }

        onClose()
    })

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="400px" overflowY="visible">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center">
                        <ModalTitle>{title}</ModalTitle>
                        <Button shape="square" variant="ghost" onClick={onClose}>
                            <XCloseIcon />
                        </Button>
                    </Flexbox>
                </ModalHeader>
                <ModalBody ref={modalBodyRef}>
                    <Flexbox direction="column" render="form" fullWidth gap={4}>
                        <Controller
                            control={form.control}
                            name="type"
                            render={({field}) => (
                                <InputSelect
                                    label={t('commons:qualification')}
                                    optionsMap={userQualificationToLabel}
                                    sameWidth
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorMessage={form.formState.errors.type?.message}
                                    portalRef={modalBodyRef}
                                    isPortal={false}
                                />
                            )}
                        />

                        <InputText
                            type="text"
                            label={t('users:institute')}
                            placeholder={t('users:institute_placeholder')}
                            errorMessage={form.formState.errors.institute?.message}
                            {...form.register('institute')}
                        />

                        <InputText
                            type="text"
                            label={t('users:qualification_sector')}
                            placeholder={t('users:qualification_sector_placeholder')}
                            errorMessage={form.formState.errors.sector?.message}
                            {...form.register('sector')}
                        />

                        <Controller
                            control={form.control}
                            name="graduationYear"
                            render={({field}) => (
                                <InputSelect
                                    label={t('users:graduation_year')}
                                    optionsMap={generateYearOptions(new Date(), {yearsBefore: 70})}
                                    sameWidth
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorMessage={form.formState.errors.graduationYear?.message}
                                    isPortal={false}
                                    reverseOrder
                                />
                            )}
                        />

                        <TextArea
                            placeholder={t('users:more_informations_placeholder')}
                            label={t(`commons:more_informations`)}
                            isOptional
                            {...form.register('otherInformation')}
                        />
                    </Flexbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose}>
                        {t('commons:cancel')}
                    </Button>
                    <Button variant="primaryNeutral" onClick={handleSubmit}>
                        {t('commons:confirm')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
